/* GENERATED FILE — DO NOT EDIT.
 *
 * The equipment vocabulary, emitted from the enum the phone compiles:
 *   HybridTrainer/Models/Enums/CanonicalEquipment.swift
 *
 * Regenerate:  KATA_EMIT_WEB_TOKENS=1 ./tools/engine-tests/run.sh
 * A harness ruler regenerates this in memory on every run and fails if it differs.
 *
 * `coach_exercises.equipment` stores these RAW VALUES. Measured live: the column is
 * populated on 108 of 108 library rows, holds 25 distinct tokens, and every one of
 * the 25 is a case below — so the builder can name what a workout needs without
 * anybody typing a display name into TypeScript, which is how two vocabularies for
 * one concept get started.
 *
 * AMBIENT is the enum's own `universallyAvailable`, in its own words: an exercise
 * requiring only these needs no equipment. It is not a convenience list — a chip
 * reading `No Equipment` or `Wall` tells a coach nothing they can act on, and
 * `none` alone is live on 36 of the 108 rows.
 */

/** Every `CanonicalEquipment` raw value, with the name the phone shows for it. */
export const EQUIPMENT_LABEL: Readonly<Record<string, string>> = {
  "ab_machine": "Ab Machine",
  "ab_wheel": "Ab Wheel",
  "adjustable_bench": "Adjustable Bench",
  "ankle_weights": "Ankle Weights",
  "assault_bike": "Assault / Air Bike",
  "back_extension": "Back Extension Bench",
  "barbell": "Barbell",
  "bike": "Exercise Bike",
  "box": "Plyo Box",
  "cable_machine": "Cable Machine",
  "cable_row": "Seated Cable Row",
  "calf_raise_machine": "Calf Raise Machine",
  "chains": "Lifting Chains",
  "chest_press_machine": "Chest Press Machine",
  "chest_supported_row": "Chest-Supported Row",
  "climbing_rope": "Climbing Rope",
  "dip_bars": "Dip Bars",
  "dip_belt": "Weight Belt",
  "doorway": "Doorway",
  "dumbbells": "Dumbbells",
  "ez_bar": "EZ Curl Bar",
  "flat_bench": "Flat Bench",
  "floor": "Floor",
  "foam_roller": "Foam Roller",
  "gg_forearm_finisher": "Forearm Finisher",
  "gg_popeyes_pronator": "Popeye's Pronator",
  "gg_riser": "Riser",
  "gg_wrist_wrench": "Angular Wrist Wrench",
  "glute_ham_raise": "Glute-Ham Raise",
  "hack_squat": "Hack Squat",
  "jump_rope": "Jump Rope",
  "kettlebell": "Kettlebell",
  "lat_pulldown": "Lat Pulldown",
  "leg_curl": "Leg Curl",
  "leg_extension": "Leg Extension",
  "leg_press": "Leg Press",
  "massage_ball": "Massage / Lacrosse Ball",
  "mat": "Yoga Mat",
  "medicine_ball": "Medicine Ball",
  "none": "No Equipment",
  "parallel_bars": "Parallel Bars",
  "parallettes": "Parallettes",
  "pec_deck": "Pec Deck / Chest Fly",
  "power_rack": "Power Rack",
  "preacher_bench": "Preacher Bench",
  "pull_up_bar": "Pull-Up Bar",
  "raised_surface": "Chair / Step / Raised Surface",
  "resistance_band": "Resistance Band",
  "rice_bucket": "Rice Bucket",
  "rings": "Gymnastic Rings",
  "roman_chair": "Roman Chair",
  "rower": "Rowing Machine",
  "sandbag": "Sandbag",
  "shoulder_press_machine": "Shoulder Press Machine",
  "sled": "Sled",
  "smith_machine": "Smith Machine",
  "squat_rack": "Squat Rack",
  "stairs": "Stairs",
  "suspension_trainer": "TRX / Suspension Trainer",
  "trap_bar": "Trap / Hex Bar",
  "treadmill": "Treadmill",
  "vertical_pole": "Pole / Vertical Object",
  "wall": "Wall",
  "weight_plate": "Weight Plates",
  "weight_vest": "Weight Vest",
  "wrist_roller": "Wrist Roller",
  "yoga_wheel": "Yoga Wheel",
};

/** Kit nobody owns or fails to own — excluded from any "what you need" summary. */
export const EQUIPMENT_AMBIENT: readonly string[] = [
  "doorway",
  "floor",
  "none",
  "raised_surface",
  "stairs",
  "vertical_pole",
  "wall",
];

/** How many items the vocabulary carries — stated so a LOSSY emitter cannot pass a
 *  byte comparison against its own output. A dropped row reads as a workout needing
 *  one less thing, which is a cleaner-looking answer than the truth. */
export const EQUIPMENT_COUNT = 67;

/** The phone picker's groups, in its order — what a coach is OFFERED. Retired, admin-only
 *  and not-a-piece (`No Equipment`) keys are absent, as they are from the phone's picker; a
 *  stored row that holds one still shows it, because the web draws what a row holds as well. */
export const EQUIPMENT_GROUPS: readonly { readonly name: string; readonly keys: readonly string[] }[] = [
  { name: "Bodyweight", keys: ["raised_surface", "doorway", "floor", "vertical_pole", "stairs", "wall"] },
  { name: "Bars & Suspension", keys: ["climbing_rope", "dip_bars", "rings", "parallel_bars", "parallettes", "pull_up_bar", "suspension_trainer"] },
  { name: "Free Weights", keys: ["ankle_weights", "barbell", "dumbbells", "ez_bar", "kettlebell", "chains", "trap_bar", "dip_belt", "weight_plate", "weight_vest"] },
  { name: "Racks & Benches", keys: ["adjustable_bench", "back_extension", "flat_bench", "glute_ham_raise", "box", "power_rack", "preacher_bench", "roman_chair", "smith_machine", "squat_rack"] },
  { name: "Machines", keys: ["ab_machine", "cable_machine", "calf_raise_machine", "chest_press_machine", "chest_supported_row", "hack_squat", "lat_pulldown", "leg_curl", "leg_extension", "leg_press", "pec_deck", "cable_row", "shoulder_press_machine"] },
  { name: "Conditioning", keys: ["bike", "jump_rope", "rower", "treadmill"] },
  { name: "Accessories", keys: ["ab_wheel", "foam_roller", "massage_ball", "resistance_band", "mat", "yoga_wheel"] },
];

/** Stored keys that are NOT a piece of equipment (`CanonicalEquipment.notAPiece` — his 2026-09-28 word: *"no
 *  equipment is not equipment"*). A row holding one needs nothing; it is never listed, offered or captioned. */
export const EQUIPMENT_NOT_A_PIECE: readonly string[] = [
  "none",
];
